import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_URL } from '../../utils/api';
import './SupportBox.css';

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

const QUICK_AMOUNTS = [20, 50, 100, 200];

/**
 * Inner form rendered inside Stripe Elements context.
 * Handles the actual payment confirmation flow.
 */
function StripeForm({ onSuccess }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    setLoading(false);

    if (result.error) {
      setError(result.error.message);
    } else if (result.paymentIntent?.status === 'succeeded') {
      onSuccess();
    }
  };

  return (
    <form className="support-form" onSubmit={handleSubmit}>
      <div className="support-payment-element">
        <PaymentElement />
      </div>
      {error && <div className="support-error">{error}</div>}
      <button
        className="support-pay-btn"
        type="submit"
        disabled={loading || !stripe}
      >
        {loading ? t('donate.processing') : t('donate.donate')}
      </button>
    </form>
  );
}

/**
 * PayPal Buttons rendered via the PayPal JS SDK.
 * Loads the SDK script dynamically and renders buttons into a container.
 */
function PayPalButtons({ amount, message, clientId, onSuccess }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [error, setError] = useState('');
  const [sdkReady, setSdkReady] = useState(false);

  // Load PayPal SDK script
  useEffect(() => {
    if (!clientId) return;

    const existingScript = document.getElementById('paypal-sdk');
    if (existingScript) {
      if (window.paypal) {
        setSdkReady(true);
      }
      return;
    }

    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=SEK`;
    script.onload = () => setSdkReady(true);
    document.body.appendChild(script);
  }, [clientId]);

  // Render buttons when SDK is ready
  useEffect(() => {
    if (!sdkReady || !window.paypal || !containerRef.current) return;

    // Clear previous buttons
    containerRef.current.innerHTML = '';

    window.paypal.Buttons({
      style: {
        color: 'black',
        shape: 'rect',
        label: 'donate',
        height: 40,
        layout: 'vertical',
      },
      createOrder: async () => {
        try {
          const r = await fetch(`${API_URL}/api/donations/paypal/create-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ amount, message }),
          });
          if (!r.ok) throw new Error('Failed');
          const data = await r.json();
          return data.order_id;
        } catch {
          setError(t('donate.paypalOrderFailed'));
          throw new Error('Create order failed');
        }
      },
      onApprove: async (data) => {
        try {
          const r = await fetch(`${API_URL}/api/donations/paypal/capture-order/${data.orderID}`, {
            method: 'POST',
          });
          if (!r.ok) throw new Error('Failed');
          const result = await r.json();
          if (result.status === 'COMPLETED') {
            onSuccess();
          }
        } catch {
          setError(t('donate.paymentFailed'));
        }
      },
      onError: () => {
        setError(t('donate.paypalCancelled'));
      },
    }).render(containerRef.current);
  }, [sdkReady, amount, message, onSuccess, t]);

  return (
    <div className="support-paypal-container">
      {error && <div className="support-error">{error}</div>}
      <div ref={containerRef} className="support-paypal-buttons" />
      {!sdkReady && <div className="support-paypal-loading">{t('donate.loadingPaypal')}</div>}
    </div>
  );
}

/**
 * Complete donation component with amount selection, message input,
 * and Stripe PaymentElement + PayPal Buttons integration.
 */
export default function SupportBox({ compact }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [method, setMethod] = useState(null); // 'stripe' | 'paypal'
  const [clientSecret, setClientSecret] = useState(null);
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/donations/config`)
      .then(r => r.json())
      .then(data => {
        setConfig(data);
        // Default to whichever is available
        if (data.stripe) setMethod('stripe');
        else if (data.paypal) setMethod('paypal');
      })
      .catch(() => {});
  }, []);

  const effectiveAmount = customAmount ? Number(customAmount) : amount;

  const handleCreateIntent = useCallback(async () => {
    if (effectiveAmount < 10) return;
    setCreating(true);
    try {
      const r = await fetch(`${API_URL}/api/donations/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: effectiveAmount, message }),
      });
      if (!r.ok) throw new Error('Failed');
      const data = await r.json();
      setClientSecret(data.client_secret);
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  }, [effectiveAmount, message]);

  const handleSuccess = useCallback(() => {
    setSuccess(true);
    setClientSecret(null);
  }, []);

  const handleReset = () => {
    setSuccess(false);
    setClientSecret(null);
    setMessage('');
    setCustomAmount('');
    setAmount(50);
  };

  if (!config?.enabled) return null;

  const hasStripe = config.stripe && stripePromise;
  const hasPaypal = config.paypal && config.paypal_client_id;
  const hasBoth = hasStripe && hasPaypal;

  if (success) {
    return (
      <div className={`support-box${compact ? ' support-box-compact' : ''}`}>
        <div className="support-success">
          <span className="support-success-icon">&#10003;</span>
          <h3 className="support-success-title">{t('donate.thanksSupport')}</h3>
          <p className="support-success-text">{t('donate.donationMeans')}</p>
          <button className="support-reset-btn" onClick={handleReset}>
            {t('donate.donateAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`support-box${compact ? ' support-box-compact' : ''}`}>
      <h3 className="support-title">{t('donate.supportTitle')}</h3>
      <p className="support-subtitle">
        {t('donate.supportSubtitle')}
      </p>

      {/* Amount selection - always shown when no Stripe checkout active */}
      {!clientSecret && (
        <>
          <div className="support-amounts">
            {QUICK_AMOUNTS.map((val) => (
              <button
                key={val}
                className={`support-amount-btn${!customAmount && amount === val ? ' active' : ''}`}
                onClick={() => { setAmount(val); setCustomAmount(''); }}
              >
                {val} kr
              </button>
            ))}
          </div>

          <div className="support-custom">
            <input
              type="number"
              className="support-custom-input"
              placeholder={t('donate.customAmount')}
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              min={10}
              max={10000}
            />
          </div>

          <textarea
            className="support-message"
            placeholder={t('donate.messageOptional')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            rows={2}
          />

          {/* Payment method toggle */}
          {hasBoth && (
            <div className="support-method-toggle">
              <button
                className={`support-method-btn${method === 'stripe' ? ' active' : ''}`}
                onClick={() => setMethod('stripe')}
              >
                {t('donate.cardApplePay')}
              </button>
              <button
                className={`support-method-btn${method === 'paypal' ? ' active' : ''}`}
                onClick={() => setMethod('paypal')}
              >
                PayPal
              </button>
            </div>
          )}

          {/* Stripe: continue button */}
          {method === 'stripe' && hasStripe && (
            <button
              className="support-continue-btn"
              onClick={handleCreateIntent}
              disabled={creating || effectiveAmount < 10}
            >
              {creating ? t('donate.loading') : t('donate.continueAmount', { amount: effectiveAmount })}
            </button>
          )}

          {/* PayPal: inline buttons */}
          {method === 'paypal' && hasPaypal && effectiveAmount >= 10 && (
            <PayPalButtons
              amount={effectiveAmount}
              message={message}
              clientId={config.paypal_client_id}
              onSuccess={handleSuccess}
            />
          )}
        </>
      )}

      {/* Stripe checkout (after clicking continue) */}
      {clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#ff6b00',
                colorBackground: '#15151f',
                colorText: '#ffffff',
                colorDanger: '#ef4444',
                fontFamily: 'Rajdhani, sans-serif',
                borderRadius: '8px',
              },
            },
          }}
        >
          <div className="support-paying-amount">
            {effectiveAmount} kr
          </div>
          <StripeForm onSuccess={handleSuccess} />
          <button
            className="support-back-btn"
            onClick={() => setClientSecret(null)}
          >
            {t('profile.back')}
          </button>
        </Elements>
      )}
    </div>
  );
}
